import React, { useEffect } from 'react';
import clsx from 'clsx';
import _ from 'underscore';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Button, Card, CardContent, CardActions, Grid, Divider, TextField, colors } from '@mui/material';
import { SelectField, FilePicker, Checkbox } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';
import validate from 'validate.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { enqueueSnackbar } from 'actions';
import { SiteAssessment7Days, SetFormState } from '../../actions/SiteAssessment7DaysActions';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	site: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	bands: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	startDate: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	periodDays: {
		presence: { allowEmpty: false, message: 'is required' },
	}

};

const bands = [
	{ value: 'All', text: 'All Bands' },
	{ value: 'L800', text: 'L800' },
	{ value: 'L1800', text: 'L1800' },
	{ value: 'L2600', text: 'L2600' }
];

const reportType = [
	{ value: 'Site Acceptance', text: 'Site Acceptance' },
	{ value: 'Site Selection', text: 'Site Selection' }
];

const SiteAssessment7DaysForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.SiteAssessmentReducer.formState);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	const user = useSelector(state => state.session.user);
	const _generate = () => {
		dispatch(SiteAssessment7Days(formState.values, user.id));
	};

	return (
		<Card className={classes.content}>
			<CardContent>
				<Grid container spacing={1}>
					<Grid item md={12} xs={12}>
						<TextField
							fullWidth
							label="Site(s) List"
							name="site"
							onChange={_handleChange}
							value={formState.values.site}
							variant="outlined"
							error={_hasError('site')}
							helperText={
								_hasError('site') ? formState.errors.site[0] : 'Example: 10500,10501,10502'
							}
						/>
					</Grid>
				</Grid>
				<br />
				<Grid container spacing={1}>
					<Grid item md={4} xs={12}>
						<SelectField
							id="reportType"
							name="reportType"
							value={formState.values.reportType || ''}
							onChange={_handleChange}
							hintText="Report"
							menuItems={reportType}
							error={_hasError('reportType')}
							helperText={_hasError('reportType') ? formState.errors.reportType[0] : null}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<SelectField
							id="bands"
							name="bands"
							value={formState.values.bands || ''}
							onChange={_handleChange}
							hintText="Band"
							menuItems={bands}
							error={_hasError('bands')}
							helperText={_hasError('bands') ? formState.errors.bands[0] : null}
						/>
					</Grid>
					<Grid item md={2} xs={12}>
						<TextField
							fullWidth
							label="Period"
							name="periodDays"
							onChange={_handleChange}
							value={formState.values.periodDays}
							variant="outlined"
							error={_hasError('periodDays')}
							helperText={
								_hasError('periodDays') ? formState.errors.periodDays[0] : 'Example: 7'
							}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<DatePicker
							label="Implementation Date"
							name="startDate"
							value={formState.values.startDate || null}
							onChange={(value) => _handleDateChange(value, 'startDate')}
							error={_hasError('startDate')}
							helperText={_hasError('startDate') ? formState.errors.startDate[0] : null}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generate} variant="contained" disabled={!formState.isValid}>Export</Button>
			</CardActions>
		</Card>
	);
};

export default SiteAssessment7DaysForm;
